import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginError, useSessionStore } from '@features/auth';
import { retryAfter } from '../utils/retryAfter';
import { LoginCodeStep, type SecondFactor } from './LoginCodeStep';
import { Input } from '@core-ui/Components/ui/input';
import { Button } from '@core-ui/Components/ui/button';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import { TendrilColorSloganTyped } from '@assets/index';
import { privateRoutes } from '@constants';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@core-ui-lib/utils';
import ReactGA from 'react-ga4';

import { logInfo } from '@lib/logger/logInfo';
import { logWarning } from '@lib/logger/logWarning';
import { logError } from '@lib/logger/logError';
import { logSuccess } from '@lib/logger/logSuccess';
import { useFeatureFlagsStore } from '@store/featureFlags';

const loginSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'loginForm.errors.emailRequired' })
    .email({ message: 'loginForm.errors.emailInvalid' }),
  password: z
    .string()
    .min(1, { message: 'loginForm.errors.passwordRequired' })
    .min(6, { message: 'loginForm.errors.passwordMin' }),
  remember: z.boolean().optional().default(false),
});

type LoginFormData = z.infer<typeof loginSchema>;

const SOURCE = 'auth/LoginForm';

const redactEmail = (email: string): string => {
  const trimmed = email.trim();
  const [user, domain] = trimmed.split('@');
  if ((user === '') || (domain === '')) {
    return 'invalid-email';
  }
  const u = user.length <= 2 ? `${user[0] ?? '*'}*` : `${user.slice(0, 2)}***`;
  const dParts = domain.split('.');
  const d0 = (dParts[0] !== '') ? `${dParts[0].slice(0, 2)}***` : 'do***';
  const d1 = (dParts.slice(1).join('.') !== '') || '*';
  return `${u}@${d0}.${d1}`;
};

const safeErrorToCtx = (err: unknown): Record<string, unknown> => {
  if (err instanceof Error) {
    return {
      name: err.name,
      message: err.message,
      stack: err.stack,
    };
  }
  return { err };
};

export const LoginForm: React.FC = () => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorApi, setErrorApi] = useState('');
  // Set from a 429 `login_locked` (tendril-connect#2266): the time the email
  // is locked until. The button stays disabled and the message counts down.
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  // Set once auth answers that the account needs a two-factor code. The
  // credentials are kept in memory only, to send them again with the code.
  const [pendingCredentials, setPendingCredentials] = useState<LoginFormData | null>(null);
  const navigate = useNavigate();
  const startSession = useSessionStore((state) => state.startSession);
  const clearAndLoad = useFeatureFlagsStore((state) => state.clearAndLoad);

  useEffect(() => {
    if (lockedUntil == null) {
      return;
    }

    const refresh = (): void => {
      const minutes = Math.ceil((lockedUntil - Date.now()) / 60_000);
      if (minutes <= 0) {
        setLockedUntil(null);
        setErrorApi('');
        return;
      }
      const { unit, count } = retryAfter(minutes);
      setErrorApi(t(unit === 'hours' ? 'loginForm.errors.lockedHours' : 'loginForm.errors.locked', { count }));
    };

    refresh();
    const timer = window.setInterval(refresh, 15_000);
    return () => {
      window.clearInterval(timer);
    };
  }, [lockedUntil, t]);

  ReactGA.send({ hitType: 'pageview', page: window.location.pathname });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitted, touchedFields },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { remember: false },
  });

  const attemptLogin = async (
    data: LoginFormData,
    secondFactor: SecondFactor = {},
  ): Promise<void> => {
    setLoading(true);
    setErrorApi('');

    const ctxBase = {
      email: redactEmail(data.email),
      remember: data.remember,
    } as const;

    await logInfo('Login attempt started', SOURCE, ctxBase);

    try {
      const { email, password } = data;

      await startSession({ email, password, ...secondFactor });

      const user = useSessionStore.getState().currentUser;


      if (user == null) {
        await logWarning(
          'Login completed but currentUser is null (invalid credentials or missing state update)',
          SOURCE,
          ctxBase,
          false
        );

        setErrorApi(t('loginForm.errors.invalidCredentials'));
        return;
      }

      const roles = Array.isArray(user.type) ? user.type : [];

      ReactGA.event( 'login',
        {
          path: window.location.pathname,
          userId: user.userId,
          organizationId: user.organizationId,
          role: roles,
        }
      );

      await logSuccess('Login successful', SOURCE, {
        ...ctxBase,
        userId: user.userId ?? undefined,
        roles,
      }, false);

      void clearAndLoad(user.organizationId);

      if (roles.includes('admin') || roles.includes('sdr')) {
        await logInfo('Navigating after login: WIZARD', SOURCE, {
          ...ctxBase,
          roles,
          route: privateRoutes.WIZARD,
        });

        navigate(`/${privateRoutes.WIZARD}`, { replace: true });
        return;
      }

      if (roles.includes('agent')) {
        await logInfo('Navigating after login: AGENT', SOURCE, {
          ...ctxBase,
          roles,
          route: privateRoutes.AGENT,
        });

        navigate(`/${privateRoutes.AGENT}`, { replace: true });
        return;
      }

      await logWarning(
        'User logged in with unknown role(s). Using WIZARD fallback route',
        SOURCE,
        { ...ctxBase, roles },
        false
      );

      navigate(`/${privateRoutes.WIZARD}`, { replace: true }); // fallback
    } catch (err) {
      const code = err instanceof LoginError ? err.code : undefined;

      if (code === 'totp_required') {
        // Not a failure: the password was right and the account has
        // two-factor authentication. Ask for the code.
        await logInfo('Login needs a two-factor code', SOURCE, ctxBase);
        setPendingCredentials(data);
        return;
      }

      if (code === 'login_locked') {
        // Too many wrong passwords: wait, the message says how long.
        const minutes = err instanceof LoginError ? (err.retryAfterMinutes ?? 1) : 1;
        await logWarning('Login locked after wrong passwords', SOURCE, { ...ctxBase, minutes }, false);
        setLockedUntil(Date.now() + minutes * 60_000);
        return;
      }

      if (code === 'totp_invalid' || code === 'totp_locked') {
        await logWarning(`Login two-factor code rejected: ${code}`, SOURCE, ctxBase, false);
        setErrorApi(
          t(code === 'totp_locked'
            ? 'loginForm.twoFactor.errors.locked'
            : 'loginForm.twoFactor.errors.invalid'),
        );
        return;
      }

      await logError(
        'Login failed: startSession threw an error',
        SOURCE,
        { ...ctxBase, ...safeErrorToCtx(err) },
        false
      );

      setErrorApi(t('loginForm.errors.invalidCredentials'));
    } finally {
      setLoading(false);
      await logInfo('Login attempt finished', SOURCE, ctxBase);
    }
  };

  const onSubmit = async (data: LoginFormData): Promise<void> => {
    await attemptLogin(data);
  };

  if (pendingCredentials != null) {
    return (
      <LoginCodeStep
        loading={loading}
        error={errorApi}
        onSubmit={async (secondFactor) => {
          await attemptLogin(pendingCredentials, secondFactor);
        }}
        onBack={() => {
          setPendingCredentials(null);
          setErrorApi('');
        }}
      />
    );
  }

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e);
      }}
      noValidate
      className="w-full max-w-md space-y-6 px-4 sm:px-0 text-foreground"
    >
      <div className="text-center space-y-1">
        <img
          src={TendrilColorSloganTyped}
          alt="Tendril logo"
          className="mx-auto h-12 w-auto mb-6"
        />
        <h2 className="text-2xl font-semibold">
          {t('loginForm.Login to your account')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('loginForm.Enter your credentials below')}
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-1">
          <Input
            data-qa-id="input-email-login"
            type="email"
            placeholder={t('loginForm.Email address')}
            {...register('email')}
            disabled={loading}
            className={cn(
              (isSubmitted || touchedFields.email === true) &&
              errors.email != null &&
              '!border-error !ring-0 !outline-none focus:!ring-error focus:!border-error',
            )}
            aria-invalid={!(errors.email == null)}
            aria-describedby="email-error"
          />
          {errors.email?.message != null && (
            <p
              data-qa-id="email-error"
              className="text-error text-sm mt-1 ml-1"
            >
              {t(errors.email.message)}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <div className="relative">
            <Input
              data-qa-id="input-password-login"
              type={showPassword ? 'text' : 'password'}
              placeholder={t('loginForm.Password')}
              {...register('password')}
              disabled={loading}
              className={cn(
                'pr-10',
                (isSubmitted || touchedFields.password === true) &&
                errors.password != null &&
                '!border-error !ring-0 !outline-none focus:!ring-error focus:!border-error',
              )}
              aria-invalid={!(errors.password == null)}
              aria-describedby="password-error"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <button
                type="button"
                onClick={() => {
                  setShowPassword((prev) => !prev);
                }}
                className="text-muted-foreground hover:text-foreground focus:outline-none"
                disabled={loading}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {errors.password?.message != null && (
            <p
              data-qa-id="password-error"
              className="text-error text-sm ml-1"
              id="password-error"
            >
              {t(errors.password.message)}
            </p>
          )}
        </div>

        {errorApi !== '' && (
          <div
            data-qa-id={lockedUntil != null ? 'login-locked' : 'incorrect-email-password'}
            role="alert"
            className="mt-2 flex items-center gap-2 rounded-md border border-error px-3 py-2 text-sm text-error font-medium"
          >
            <AlertCircle className="h-4 w-4" />
            <span>{errorApi}</span>
          </div>
        )}

        <div className="flex justify-between items-center text-sm">
          <label className="flex items-center gap-2">
            <input
              data-qa-id="remember-me-checkbox"
              type="checkbox"
              {...register('remember')}
              className="accent-primary"
              disabled={loading}
            />
            {t('loginForm.Remember me')}
          </label>
          <a
            href="#"
            className="text-primary hover:underline"
            data-qa-id="forgot-password-login"
          >
            {t('loginForm.Forgot password')}
          </a>
        </div>

        <Button
          data-qa-id="login-button"
          type="submit"
          className="w-full"
          disabled={loading || lockedUntil != null}
        >
          {loading ? t('loginForm.Logging in...') : t('loginForm.Login')}
        </Button>

        <div className="flex justify-center items-center text-sm">
          <a
            data-qa-id="schedule-session-login"
            href="/workspace/schedule/session"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            {t('loginForm.scheduleSession', 'Schedule your session here')}
          </a>
        </div>
      </div>
    </form>
  );
};

export default LoginForm;
